/* eslint-disable react/require-default-props */
import React, { useCallback, useState } from 'react';
import { Modal } from '@components/v2/Modal';
import { Button } from '@ui';
import { InlineEdit } from '@components/v2/InlineEdit';
import ColorPicker from '@components/v2/ColorPicker';
import TooltipText from '@components/v2/TooltipText';
import { translate } from '@utils/i18n';
import snakeToCapitalize from '@utils/functions/snakeToCapitalize';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import './ComponentStatusDetailModalStyles.scss';
const namespace = 'features.settings.component_statuses';
// Debounce utility function
const useDebounce = (callback, delay) => {
    const timeoutRef = React.useRef();
    return useCallback((statusId, field, value) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => callback(statusId, field, value), delay);
    }, [callback, delay]);
};
export default function ComponentStatusDetailModal({ isOpen, onClose, componentStatus, onUpdate, onDelete, isUpdating = false, isDeleting = false }) {
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    // Debounced update function (500ms delay)
    const debouncedUpdate = useDebounce((statusId, field, value) => {
        void onUpdate(statusId, field, value);
    }, 500);
    const handleFieldUpdate = (field, value) => {
        if (componentStatus) {
            debouncedUpdate(componentStatus.id, field, value);
        }
    };
    const handleDelete = () => {
        if (componentStatus) {
            setIsDeleteConfirmOpen(true);
        }
    };
    const handleConfirmDelete = () => {
        if (componentStatus) {
            void onDelete(componentStatus.id);
        }
        setIsDeleteConfirmOpen(false);
    };
    const handleCancelDelete = () => {
        setIsDeleteConfirmOpen(false);
    };
    const handleColorChange = (event) => {
        handleFieldUpdate('color', event.value);
    };
    if (!componentStatus)
        return null;
    // Check if delete button should be disabled and get tooltip
    const isDeleteDisabled = componentStatus.isSystemStatus || componentStatus.componentsCount > 0;
    const deleteTooltip = (() => {
        if (componentStatus.isSystemStatus) {
            return translate('tooltips.cannot_delete_system_status', { namespace });
        }
        if (componentStatus.componentsCount > 0) {
            return translate('tooltips.cannot_delete_status_in_use', {
                namespace,
                count: componentStatus.componentsCount
            });
        }
        return '';
    })();
    const modalTitle = (React.createElement("div", { className: "component-status-detail-modal__header" },
        React.createElement("div", { className: "component-status-detail-modal__header-content" },
            React.createElement("span", null,
                React.createElement("b", null,
                    translate('status_column', { namespace }),
                    ":"),
                " ",
                snakeToCapitalize(componentStatus.name))),
        React.createElement(TooltipText, { text: deleteTooltip, position: "top" },
            React.createElement(Button, { className: "component-status-detail-modal__delete-btn", onClick: handleDelete, disabled: isDeleteDisabled || isDeleting }, isDeleting ? (React.createElement("i", { className: "fa fa-spinner fa-spin" })) : (React.createElement(React.Fragment, null,
                React.createElement("i", { className: "fa fa-trash" }),
                " ",
                translate('delete', { namespace: 'features.commons' })))))));
    return (React.createElement(Modal, { title: modalTitle, isOpen: isOpen, onClose: onClose, width: "600px", className: "component-status-detail-modal" },
        React.createElement("div", { className: "component-status-detail-form" },
            React.createElement("div", { className: "form-section" },
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        translate('color', { namespace: 'features.commons' }),
                        ":"),
                    React.createElement("div", { className: "form-field__value" },
                        React.createElement(ColorPicker, { value: componentStatus.color, onChange: handleColorChange, disabled: componentStatus.isSystemStatus }))),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        translate('description_column', { namespace }),
                        ":"),
                    React.createElement(InlineEdit, { as: "div", className: "form-field__value form-field__value--textarea", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('description', target.textContent || '');
                        }, disableEdit: componentStatus.isSystemStatus }, componentStatus.description || ''))),
            isUpdating && (React.createElement("div", { className: "component-status-detail-modal__updating" },
                React.createElement("i", { className: "fa fa-spinner fa-spin" }),
                " ",
                translate('saving', { namespace: 'features.commons' }),
                "..."))),
        isDeleteConfirmOpen && (React.createElement(ConfirmDialog, { title: translate('delete_status_title', { namespace }), description: translate('delete_status_description', { namespace }), onConfirm: handleConfirmDelete, onCancel: handleCancelDelete, yesLabel: translate('delete', { namespace: 'features.commons' }), noLabel: translate('cancel', { namespace: 'features.commons' }) }))));
}
